import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import './PodcastDetail.css';
import { fetchPodcastNotes } from '../utils/api';
import { formatContentDate } from '../utils/date';

const PodcastDetail = ({ headingLevel = 1 }) => {
  const { id } = useParams();
  const [requestState, setRequestState] = useState(() => ({
    id,
    status: 'loading',
    data: null,
    error: null
  }));
  const TitleHeading = headingLevel === 1 ? 'h1' : 'h2';
  const SectionHeading = headingLevel === 1 ? 'h2' : 'h3';

  useEffect(() => {
    const controller = new AbortController();

    setRequestState({
      id,
      status: 'loading',
      data: null,
      error: null
    });

    const loadPodcastNotes = async () => {
      try {
        const data = await fetchPodcastNotes(id, { signal: controller.signal });
        if (!controller.signal.aborted) {
          setRequestState({
            id,
            status: 'success',
            data,
            error: null
          });
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          setRequestState({
            id,
            status: 'error',
            data: null,
            error: err.message
          });
        }
      }
    };

    loadPodcastNotes();

    return () => controller.abort();
  }, [id]);

  useEffect(() => {
    if (requestState.id === id && requestState.data?.metadata?.title) {
      document.title = `${requestState.data.metadata.title} | Alex Bock`;
    }
  }, [id, requestState]);

  const isCurrentRequest = requestState.id === id;
  const loading = !isCurrentRequest || requestState.status === 'loading';
  const error = isCurrentRequest ? requestState.error : null;
  const podcastData = isCurrentRequest ? requestState.data : null;

  const getPlainText = (richText = []) => (
    richText.map((part) => part.plain_text || '').join('')
  );

  const renderMetadata = (metadata) => {
    return (
      <header className="podcast-metadata">
        {metadata.icon?.type === 'emoji' ? (
          <div className="podcast-icon" aria-hidden="true">{metadata.icon.emoji}</div>
        ) : metadata.icon?.type === 'file' || metadata.icon?.type === 'external' ? (
          <img 
            src={metadata.icon.type === 'file' ? metadata.icon.file.url : metadata.icon.external.url}
            alt=""
            className="podcast-icon"
            width="60"
            height="60"
            decoding="async"
          />
        ) : null}
        <div className="podcast-info">
          <TitleHeading className="podcast-title">{metadata.title}</TitleHeading>
          <div className="podcast-details">
            <span className="podcast-show">{metadata.show}</span>
            {metadata.date && (
              <span className="podcast-date">
                {formatContentDate(metadata.date, {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </span>
            )}
          </div>
        </div>
      </header>
    );
  };

  const renderBlocks = (blocks = []) => {
    const renderedBlocks = [];
    let currentListType = null;
    let currentListItems = [];

    const flushList = () => {
      if (currentListItems.length === 0) return;

      const ListElement = currentListType === 'numbered_list_item' ? 'ol' : 'ul';
      renderedBlocks.push(
        <ListElement
          key={`list-${currentListItems[0].id}`}
          className="podcast-content-list"
          role="list"
        >
          {currentListItems.map((item) => renderBlock(item))}
        </ListElement>
      );
      currentListItems = [];
      currentListType = null;
    };

    blocks.forEach((block) => {
      const isListItem = block.type === 'bulleted_list_item' || block.type === 'numbered_list_item';

      if (isListItem) {
        if (currentListType && currentListType !== block.type) flushList();
        currentListType = block.type;
        currentListItems.push(block);
        return;
      }

      flushList();
      const renderedBlock = renderBlock(block);
      if (renderedBlock) renderedBlocks.push(renderedBlock);
    });

    flushList();
    return renderedBlocks;
  };

  const renderBlock = (block) => {
    const { type, id } = block;
    const value = block[type];
    const blockText = getPlainText(value?.rich_text);

    switch (type) {
      case 'heading_1':
        if (blockText.toLowerCase() === "episode show notes") {
          return (
            <details key={id} className="episode-notes">
              <summary>
                <span>▸ Episode show notes</span>
              </summary>
              {block.children && (
                <div className="episode-content">
                  {renderBlocks(block.children)}
                </div>
              )}
            </details>
          );
        }
        if (blockText.toLowerCase() === "your snips") {
          return (
            <SectionHeading key={id} className="podcast-heading-1">
              Your Snips
            </SectionHeading>
          );
        }
        return null;

      case 'heading_3':
        if (value.rich_text[0]?.plain_text.match(/\[\d{2}:\d{2}\]/)) {
          const timestamp = value.rich_text[0].plain_text;
          const title = getPlainText(value.rich_text.slice(1));
          
          return (
            <details key={id} className="timestamp-section">
              <summary>
                <span className="timestamp-wrapper">
                  ▸ {timestamp}
                  {title && <span className="timestamp-title">{title}</span>}
                </span>
              </summary>
              {block.children && (
                <div className="timestamp-content">
                  {renderBlocks(block.children)}
                </div>
              )}
            </details>
          );
        }
        return null;

      case 'paragraph':
        if (value.rich_text.length === 0) return null;
        const text = blockText;
        
        if (/play\s+snip/i.test(text)) return null;

        return (
          <p key={id} className="podcast-paragraph">
            {text}
          </p>
        );

      case 'bulleted_list_item':
      case 'numbered_list_item':
        return (
          <li key={id} className="podcast-list-item">
            {blockText}
            {block.children && renderBlocks(block.children)}
          </li>
        );

      case 'toggle':
        const toggleText = blockText;
        if (toggleText.includes('Transcript')) {
          return (
            <details key={id} className="podcast-toggle transcript">
              <summary>
                {toggleText}
              </summary>
              {block.children && (
                <div className="toggle-content">
                  {renderBlocks(block.children)}
                </div>
              )}
            </details>
          );
        }
        return null;

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="content-item status-message" role="status" aria-live="polite">
        <TitleHeading className="visually-hidden">Podcast notes</TitleHeading>
        Loading podcast notes…
      </div>
    );
  }

  if (error) {
    return (
      <div className="content-item status-message" role="alert">
        <TitleHeading className="visually-hidden">Podcast notes</TitleHeading>
        Unable to load podcast notes: {error}
      </div>
    );
  }

  if (!podcastData) {
    return (
      <div className="content-item status-message" role="status">
        <TitleHeading className="visually-hidden">Podcast notes</TitleHeading>
        No podcast notes were found.
      </div>
    );
  }

  const { metadata, content } = podcastData;

  return (
    <article className="podcast-detail">
      <div className="content-item">
        {renderMetadata(metadata)}
        <div className="podcast-content">
          {renderBlocks(content.results)}
        </div>
      </div>
    </article>
  );
};

export default PodcastDetail;
